import React, { useEffect, useMemo, useRef, useState } from "react";
import { Search, Plus, X } from "lucide-react";
import { Input } from "@/components/ui/input";

/**
 * Searchable autocomplete for saved locations (Customer/Consignee address book).
 * As user types → matches (label / name / city / phone / street) appear.
 * Selecting a match calls `onSelect(location)`. If no match is found and the
 * user has typed something, a "+ Create new" row appears — clicking it calls
 * `onCreateNew(query)`.
 *
 * Groups results into "Customer's locations" (if any) and "Directory".
 */
export default function LocationAutocomplete({
  placeholder = "Search location…",
  options = [],            // shared library
  customerOptions = [],    // scoped to active customer
  onSelect,
  onCreateNew,
  createLabel = "Create new",
  dataTestId,
  disabled = false,
}) {
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(0);
  const wrapRef = useRef(null);

  useEffect(() => {
    const onDoc = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const scoreMatch = (l, s) => {
    if (!s) return 1;
    const hay = [l.label, l.name, l.street, l.city, l.state, l.zip, l.contact_name, l.contact_phone]
      .filter(Boolean).join(" ").toLowerCase();
    return hay.includes(s) ? 1 : 0;
  };

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    const cust = customerOptions.filter((l) => scoreMatch(l, s));
    const shared = options.filter((l) => !l.customer_id && scoreMatch(l, s));
    return { cust, shared, total: cust.length + shared.length };
  }, [q, options, customerOptions]);

  const flat = [...filtered.cust, ...filtered.shared];
  const showCreate = q.trim().length > 0 && !!onCreateNew;
  const totalRows = flat.length + (showCreate ? 1 : 0);

  const handleKey = (e) => {
    if (!open) return;
    if (e.key === "ArrowDown") { e.preventDefault(); setHighlight((h) => Math.min(totalRows - 1, h + 1)); }
    if (e.key === "ArrowUp") { e.preventDefault(); setHighlight((h) => Math.max(0, h - 1)); }
    if (e.key === "Enter") {
      e.preventDefault();
      if (highlight < flat.length) {
        onSelect?.(flat[highlight]);
        setQ("");
        setOpen(false);
      } else if (showCreate && highlight === flat.length) {
        onCreateNew?.(q.trim());
        setQ("");
        setOpen(false);
      }
    }
    if (e.key === "Escape") { setOpen(false); }
  };

  return (
    <div className="relative" ref={wrapRef}>
      <div className="relative">
        <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-zinc-400" />
        <Input
          value={q}
          onChange={(e) => { setQ(e.target.value); setOpen(true); setHighlight(0); }}
          onFocus={() => setOpen(true)}
          onKeyDown={handleKey}
          placeholder={placeholder}
          className="sharp h-7 text-[11px] pl-7 pr-7 bg-white w-56"
          disabled={disabled}
          data-testid={dataTestId}
        />
        {q && (
          <button
            type="button"
            onClick={() => { setQ(""); setOpen(false); }}
            className="absolute right-1 top-1/2 -translate-y-1/2 p-0.5 text-zinc-400 hover:text-zinc-700"
            tabIndex={-1}
          >
            <X className="h-3 w-3" />
          </button>
        )}
      </div>
      {open && (
        <div className="absolute right-0 top-full mt-1 z-50 w-80 max-h-72 overflow-y-auto bg-white border border-zinc-950 shadow-lg text-xs">
          {filtered.cust.length > 0 && (
            <>
              <div className="px-2 py-1 text-[9px] font-mono uppercase text-zinc-500 bg-zinc-50">
                Customer&apos;s locations
              </div>
              {filtered.cust.map((l, i) => (
                <LocRow key={l.id} l={l} idx={i} highlight={highlight} onSelect={() => { onSelect?.(l); setQ(""); setOpen(false); }} />
              ))}
            </>
          )}
          {filtered.shared.length > 0 && (
            <>
              <div className="px-2 py-1 text-[9px] font-mono uppercase text-zinc-500 bg-zinc-50 border-t border-zinc-200">
                Directory
              </div>
              {filtered.shared.map((l, i) => (
                <LocRow key={l.id} l={l} idx={filtered.cust.length + i} highlight={highlight} onSelect={() => { onSelect?.(l); setQ(""); setOpen(false); }} />
              ))}
            </>
          )}
          {flat.length === 0 && !showCreate && (
            <div className="px-3 py-3 text-zinc-500">No matches.</div>
          )}
          {showCreate && (
            <button
              type="button"
              onClick={() => { onCreateNew?.(q.trim()); setQ(""); setOpen(false); }}
              className={`w-full text-left px-3 py-2 border-t border-zinc-200 text-[#0056B3] hover:bg-blue-50 inline-flex items-center gap-1 ${highlight === flat.length ? "bg-blue-50" : ""}`}
              data-testid="loc-autocomplete-create"
            >
              <Plus className="h-3 w-3" /> {createLabel}: <b className="ml-1">&ldquo;{q.trim()}&rdquo;</b>
            </button>
          )}
        </div>
      )}
    </div>
  );
}

function LocRow({ l, idx, highlight, onSelect }) {
  return (
    <button
      type="button"
      onClick={onSelect}
      className={`w-full text-left px-3 py-1.5 hover:bg-blue-50 ${highlight === idx ? "bg-blue-50" : ""}`}
      data-testid={`loc-autocomplete-row-${l.id}`}
    >
      <div className="font-semibold text-[12px]">{l.label}</div>
      <div className="text-[10px] text-zinc-600 truncate">
        {[l.name, l.city, l.state].filter(Boolean).join(", ")}
        {l.contact_phone ? <span className="ml-2 font-mono">{l.contact_phone}</span> : null}
      </div>
    </button>
  );
}
